import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearCountryDetails } from "./countrySlice";
import {
  createCountry,
  getAllCountries,
  getCountryByID,
  updateCountry,
} from "./services/country.services";
import { closed } from "../../components/drawer/drawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";

const CreateCountry: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CountryDetail = useAppSelector((state) => state.country.CountryDetail);
  const CountryID = useAppSelector((state) => state.drawer.data);
  const formLoading = useAppSelector((state) => state.country.formLoading);
  const [formKey, setFormKey] = React.useState<number>(1);

  useEffect(() => {
    return () => {
      dispatch(clearCountryDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    if (CountryID) {
      dispatch(getCountryByID(CountryID));
    }
  }, [CountryID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [CountryDetail]);

  const handleSubmit = async (values: any) => {
    try {
      if (!CountryID) {
        const insertPayload = {
          CountryName: values.CountryName,
          CountryCode: values.CountryCode,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createCountry(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCountryDetails());
          dispatch(getAllCountries());
        }
      } else {
        const editPayload = {
          ID: CountryID,
          CountryName: values.CountryName,
          CountryCode: values.CountryCode,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateCountry(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearCountryDetails());
          dispatch(getAllCountries());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={CountryDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {CountryID ? "Update Country" : "Add Country"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                  icon={RxCross1}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="CountryName"
                  label="Country Name"
                  placeholder="i.e. india"
                  component={FormTextField}
                  astrike={true}
                  maxLength={50}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="CountryCode"
                  label="Country Code"
                  placeholder="i.e IND"
                  component={FormTextField}
                  astrike={true}
                  type={"text"}
                  maxLength={10}
                  validator={requiredValidator}
                />
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                colSpan={3}
                style={{
                  textAlign: "end",
                  marginTop: 20,
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={CountryID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateCountry;
